import { useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  User,
  MessageCircle,
  Send,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useLanguage } from "@/hooks/useLanguage";
import { COMPANY } from "@/data/site";

const inquiryTypeKeys = [
  "quotationRequest",
  "vendorRegistration",
  "industrialSupplyOpt",
  "electricalSupplyOpt",
  "mechanicalSupplyOpt",
  "civilMaterialsOpt",
  "cncLaserOpt",
  "pressBrakeOpt",
  "tubeFabOpt",
  "steelFabOpt",
  "powderCoatingOpt",
  "epoxyCoatingOpt",
  "customFabOpt",
  "generalInquiry",
] as const;

export default function Contact() {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    email: "",
    mobile: "",
    inquiryType: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = t("fullName") + " " + t("isRequired");
    if (!formData.email.trim()) newErrors.email = t("emailLabel") + " " + t("isRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t("validEmail");
    if (!formData.inquiryType) newErrors.inquiryType = t("selectInquiry");
    if (!formData.message.trim()) newErrors.message = t("message") + " " + t("isRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
    }
  };

  return (
    <>
      <PageHeader titleKey="contactTitle" subtitleKey="contactSubtitle" />

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("headOffice")}</h3>
                <div className="flex items-start gap-3 text-steel mb-3">
                  <MapPin className="w-4 h-4 mt-1 shrink-0 text-gold" />
                  <span className="text-sm leading-relaxed">{COMPANY.address.full}</span>
                </div>
                <p className="text-xs text-steel ml-7">{t("shortAddress")}: {COMPANY.address.short}</p>
              </div>

              <div>
                <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("contactPerson")}</h3>
                <div className="flex items-center gap-3 text-steel mb-2">
                  <User className="w-4 h-4 shrink-0 text-gold" />
                  <span className="text-sm font-medium text-graphite">{COMPANY.contactPerson.name}</span>
                </div>
                <p className="text-xs text-steel ml-7">{COMPANY.contactPerson.designation}</p>
              </div>

              <div>
                <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("mobileWhatsapp")}</h3>
                <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-3 text-steel hover:text-secondary-blue transition-colors">
                  <Phone className="w-4 h-4 shrink-0 text-gold" />
                  <span className="text-sm">{COMPANY.phone}</span>
                </a>
              </div>

              <div>
                <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("email")}</h3>
                <div className="space-y-2">
                  <a href={`mailto:${COMPANY.email.direct}`} className="flex items-center gap-3 text-steel hover:text-secondary-blue transition-colors">
                    <Mail className="w-4 h-4 shrink-0 text-gold" />
                    <span className="text-sm">{COMPANY.email.direct}</span>
                  </a>
                  <a href={`mailto:${COMPANY.email.general}`} className="flex items-center gap-3 text-steel hover:text-secondary-blue transition-colors">
                    <Mail className="w-4 h-4 shrink-0 text-gold" />
                    <span className="text-sm">{COMPANY.email.general}</span>
                  </a>
                </div>
              </div>

              <div>
                <h3 className="font-display font-bold text-lg text-primary-deep mb-4">{t("businessHours")}</h3>
                <div className="flex items-start gap-3 text-steel">
                  <Clock className="w-4 h-4 mt-0.5 text-gold" />
                  <div className="text-sm">
                    <p>{COMPANY.hours.days}: {COMPANY.hours.time}</p>
                    <p>Friday: {COMPANY.hours.friday}</p>
                  </div>
                </div>
              </div>

              <a href={COMPANY.googleMaps} target="_blank" rel="noopener noreferrer" className="btn-primary w-full">
                <MapPin className="w-4 h-4 mr-2" />
                {t("openLocation")}
              </a>

              <div className="rounded-lg overflow-hidden border border-muted-border">
                <iframe
                  title="Sky Mark Co. Location"
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.5!2d46.6753!3d24.7136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzQ4LjgiTiA0NsKwNDAnMzEuMSJF!5e0!3m2!1sen!2ssa!4v1`}
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale-[30%]"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-lg p-6 md:p-8 border border-muted-border">
                {status === "success" ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
                      <Send className="w-7 h-7 text-success" />
                    </div>
                    <h3 className="font-display font-bold text-xl text-primary-deep mb-3">{t("thankYou")}</h3>
                    <p className="text-steel leading-relaxed max-w-md mx-auto">{t("inquiryReceived")}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="font-display font-bold text-xl text-primary-deep mb-1">{t("sendInquiryForm")}</h3>
                    <p className="text-sm text-steel mb-5">{t("fillForm")}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-graphite mb-1.5">{t("fullName")} <span className="text-red-500">*</span></label>
                        <input type="text" name="fullName" value={formData.fullName} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-md border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-blue ${errors.fullName ? "border-red-500" : "border-muted-border"}`} placeholder={t("fullNamePlaceholder")} />
                        {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-graphite mb-1.5">{t("companyName")}</label>
                        <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} className="w-full px-4 py-2.5 rounded-md border border-muted-border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-blue" placeholder={t("companyPlaceholder")} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-graphite mb-1.5">{t("emailLabel")} <span className="text-red-500">*</span></label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-md border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-blue ${errors.email ? "border-red-500" : "border-muted-border"}`} placeholder={t("emailPlaceholder")} />
                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-graphite mb-1.5">{t("mobileLabel")}</label>
                        <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className="w-full px-4 py-2.5 rounded-md border border-muted-border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-blue" placeholder={t("mobilePlaceholder")} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-graphite mb-1.5">{t("inquiryType")} <span className="text-red-500">*</span></label>
                      <select name="inquiryType" value={formData.inquiryType} onChange={handleChange} className={`w-full px-4 py-2.5 rounded-md border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-blue bg-white ${errors.inquiryType ? "border-red-500" : "border-muted-border"}`}>
                        <option value="">{t("selectInquiry")}</option>
                        {inquiryTypeKeys.map((k) => (
                          <option key={k} value={k}>{t(k)}</option>
                        ))}
                      </select>
                      {errors.inquiryType && <p className="text-xs text-red-500 mt-1">{errors.inquiryType}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-graphite mb-1.5">{t("message")} <span className="text-red-500">*</span></label>
                      <textarea name="message" value={formData.message} onChange={handleChange} rows={5} className={`w-full px-4 py-2.5 rounded-md border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-blue resize-none ${errors.message ? "border-red-500" : "border-muted-border"}`} placeholder={t("messagePlaceholder")} />
                      {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
                    </div>

                    <button type="submit" disabled={status === "submitting"} className="btn-primary w-full disabled:opacity-60">
                      {status === "submitting" ? t("sending") : <><Send className="w-4 h-4 mr-2" />{t("sendInquiryBtn")}</>}
                    </button>

                    {status === "error" && <p className="text-sm text-red-500 text-center">{t("somethingWrong")}</p>}
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-secondary-blue py-8 px-4 sm:px-6 lg:px-8">
        <div className="content-container flex flex-col sm:flex-row items-center justify-center gap-4">
          <p className="text-white text-center sm:text-left">{t("preferWhatsapp")}</p>
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-success text-white font-body font-semibold text-sm transition-all duration-300 hover:bg-green-600">
            <MessageCircle className="w-4 h-4" />
            {t("whatsappInquiry")}
          </a>
        </div>
      </section>
    </>
  );
}
